"use client";

/**
 * Inline chat card rendered when ctrl-a calls `draft_orchestration_plan`.
 *
 * Phase 1 (2026-04-21): summary + candidate list + Approve / Cancel buttons.
 * Phase 2 (2026-04-22): optional graph DAG preview + live-progress morph
 * after approve, powered by the existing useWorkflowEvents hook.
 *
 * Lifecycle:
 *  - Mount: idle. Fetch the plan's graph in the background (GET proxy).
 *    Show summary + candidates + Approve / Cancel. Graph viz is a
 *    collapsible "Show graph" section — off by default to keep the chat
 *    tight, on-tap for users who want to see what's going to run.
 *  - Click Approve: POST approve. On success the card flips to live-progress:
 *    graph stays visible, node status dots are driven by workflow_run_events.
 *  - Click Cancel: POST cancel. Card collapses to a dismissed banner.
 *
 * Modify remains Phase 3+ (backend endpoint not shipped).
 *
 * Spec: ctrl-a_workspace-main/docs/superpowers/specs/2026-04-21-orchestration-mode-design.md
 *       docs/superpowers/specs/2026-04-22-orchestration-phase-2-synthesis-graphviz-design.md
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type { WorkflowGraph } from '@/types/workflow';
import { useWorkflowEvents } from '@/hooks/use-workflow-events';
import {
  WorkflowGraphViz,
  type NodeStatus,
} from '@/components/workflows/workflow-graph-viz';

export interface OrchestrationPlanCardData {
  plan_id: string;
  summary: string | null;
  candidates: Array<{ template_id: string; name: string; score: number }>;
  note?: string | null;
}

type LocalState =
  | { kind: 'idle' }
  | { kind: 'approving' }
  | { kind: 'cancelling' }
  | { kind: 'approved'; runId: string }
  | { kind: 'cancelled' }
  | { kind: 'error'; message: string };

function authHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('ctrl-a-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function OrchestrationPlanCard({ plan }: { plan: OrchestrationPlanCardData }) {
  const [state, setState] = useState<LocalState>({ kind: 'idle' });
  const [graph, setGraph] = useState<WorkflowGraph | null>(null);
  const [graphOpen, setGraphOpen] = useState(false);

  // Fetch the plan's graph once on mount. Silent failure — the card still
  // works without it; the graph toggle just won't appear.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/orchestration/plans/${plan.plan_id}`, {
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
        });
        if (!res.ok) return;
        const data = (await res.json().catch(() => null)) as
          | { plan?: { graph?: WorkflowGraph } }
          | null;
        if (!cancelled && data?.plan?.graph) setGraph(data.plan.graph);
      } catch {
        // ignore; graph is optional enrichment
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plan.plan_id]);

  const approve = useCallback(async () => {
    setState({ kind: 'approving' });
    try {
      const res = await fetch(`/api/orchestration/plans/${plan.plan_id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
      const data = (await res.json().catch(() => null)) as
        | { workflow_run_id?: string; error?: string }
        | null;
      if (!res.ok) {
        setState({ kind: 'error', message: data?.error ?? `Approve failed (${res.status})` });
        return;
      }
      setState({ kind: 'approved', runId: data?.workflow_run_id ?? '' });
      setGraphOpen(true); // once running, the live view is the point
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Network error' });
    }
  }, [plan.plan_id]);

  const cancel = useCallback(async () => {
    setState({ kind: 'cancelling' });
    try {
      const res = await fetch(`/api/orchestration/plans/${plan.plan_id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        setState({ kind: 'error', message: data?.error ?? `Cancel failed (${res.status})` });
        return;
      }
      setState({ kind: 'cancelled' });
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Network error' });
    }
  }, [plan.plan_id]);

  if (state.kind === 'approved') {
    return (
      <LiveProgressView
        plan={plan}
        runId={state.runId}
        graph={graph}
        graphOpen={graphOpen}
        setGraphOpen={setGraphOpen}
      />
    );
  }

  if (state.kind === 'cancelled') {
    return (
      <div className="mt-3 rounded-2xl border border-[var(--outline-variant)] bg-[var(--surface-container)] px-4 py-3 flex items-center gap-2">
        <XCircle className="size-4 text-[var(--on-surface-variant)] shrink-0" />
        <p className="text-xs text-[var(--on-surface-variant)]">Plan cancelled.</p>
      </div>
    );
  }

  const busy = state.kind === 'approving' || state.kind === 'cancelling';

  return (
    <div className="mt-3 rounded-2xl border border-[var(--outline-variant)] bg-[var(--surface-container)] shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--outline-variant)]">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-[var(--primary)]" />
          <p className="text-sm font-semibold text-[var(--on-surface)]">Proposed orchestration plan</p>
        </div>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide bg-[var(--primary-muted)] text-[var(--primary)]">
          pending approval
        </span>
      </div>

      {plan.summary && (
        <div className="px-4 py-3 text-sm text-[var(--on-surface)] leading-relaxed">
          {plan.summary}
        </div>
      )}

      {plan.candidates.length > 0 && (
        <div className="px-4 pb-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--on-surface-variant)] mb-2">
            Candidate templates
          </p>
          <ul className="space-y-1">
            {plan.candidates.map((c) => (
              <li
                key={c.template_id}
                className="flex items-center justify-between text-xs text-[var(--on-surface-variant)]"
              >
                <span className="truncate">{c.name}</span>
                <span className="font-mono tabular-nums text-[10px] text-[var(--on-surface-variant)]">
                  {(c.score * 100).toFixed(0)}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {graph && (
        <GraphSection graph={graph} open={graphOpen} onToggle={() => setGraphOpen((o) => !o)} />
      )}

      {state.kind === 'error' && (
        <div className="px-4 pb-3">
          <p className="text-xs text-red-500">{state.message}</p>
        </div>
      )}

      <div className="flex items-center gap-2 px-4 py-3 border-t border-[var(--outline-variant)] bg-[var(--surface-lowest)]">
        <button
          type="button"
          onClick={approve}
          disabled={busy}
          className={cn(
            'inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all',
            'bg-[var(--primary)] text-[var(--on-primary)] hover:opacity-90 shadow-sm',
            busy && 'opacity-60 cursor-not-allowed',
          )}
        >
          {state.kind === 'approving' ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <CheckCircle2 className="size-3" />
          )}
          Approve
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={busy}
          className={cn(
            'inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all',
            'bg-[var(--surface-container)] text-[var(--on-surface)] border border-[var(--outline-variant)]',
            'hover:bg-[var(--surface-high)] hover:border-[var(--primary-muted)]',
            busy && 'opacity-60 cursor-not-allowed',
          )}
        >
          {state.kind === 'cancelling' ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <XCircle className="size-3" />
          )}
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Graph viz section (shared between pending + live states) ────────────────

function GraphSection({
  graph,
  open,
  onToggle,
  nodeStatus,
}: {
  graph: WorkflowGraph;
  open: boolean;
  onToggle: () => void;
  nodeStatus?: Record<string, NodeStatus>;
}) {
  const count = Object.keys(graph.nodes ?? {}).length;
  return (
    <div className="border-t border-[var(--outline-variant)]">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-[var(--on-surface-variant)] hover:bg-[var(--surface-high)] transition-colors"
      >
        <span>Plan graph ({count} {count === 1 ? 'node' : 'nodes'})</span>
        {open ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
      </button>
      {open && (
        <div className="px-4 pb-3 overflow-x-auto">
          <WorkflowGraphViz graph={graph} nodeStatus={nodeStatus} />
        </div>
      )}
    </div>
  );
}

// ─── Live progress view (post-approve) ───────────────────────────────────────

function LiveProgressView({
  plan,
  runId,
  graph,
  graphOpen,
  setGraphOpen,
}: {
  plan: OrchestrationPlanCardData;
  runId: string;
  graph: WorkflowGraph | null;
  graphOpen: boolean;
  setGraphOpen: (v: boolean) => void;
}) {
  const { events, isConnected } = useWorkflowEvents(runId || undefined);

  // Derive per-node status from the event stream. Node events carry node_id;
  // we fold them in timestamp order (hook returns ASC).
  const { nodeStatus, runDone, runFailed } = useMemo(() => {
    const status: Record<string, NodeStatus> = {};
    let done = false;
    let failed = false;
    for (const entry of events) {
      const e = entry.event;
      if (e.kind === 'node_started' && e.node_id) {
        status[e.node_id] = 'running';
      } else if (e.kind === 'node_completed' && e.node_id) {
        status[e.node_id] = 'completed';
      } else if (e.kind === 'node_failed' && e.node_id) {
        status[e.node_id] = 'failed';
      } else if (e.kind === 'run_completed') {
        done = true;
      } else if (e.kind === 'run_failed' || e.kind === 'run_cancelled') {
        failed = true;
      }
    }
    return { nodeStatus: status, runDone: done, runFailed: failed };
  }, [events]);

  const stateLabel = runFailed ? 'Failed' : runDone ? 'Completed' : 'Running';
  const stateClass = runFailed
    ? 'bg-red-500/10 text-red-500'
    : runDone
      ? 'bg-emerald-500/10 text-emerald-500'
      : 'bg-[var(--primary-muted)] text-[var(--primary)]';

  return (
    <div className="mt-3 rounded-2xl border border-[var(--outline-variant)] bg-[var(--surface-container)] shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--outline-variant)]">
        <div className="flex items-center gap-2">
          {runDone ? (
            <CheckCircle2 className="size-4 text-emerald-500" />
          ) : runFailed ? (
            <XCircle className="size-4 text-red-500" />
          ) : (
            <Loader2 className="size-4 text-[var(--primary)] animate-spin" />
          )}
          <p className="text-sm font-semibold text-[var(--on-surface)]">Orchestration</p>
        </div>
        <span
          className={cn(
            'text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide',
            stateClass,
          )}
        >
          {stateLabel}
        </span>
      </div>

      {plan.summary && (
        <div className="px-4 py-3 text-xs text-[var(--on-surface-variant)] leading-relaxed">
          {plan.summary}
        </div>
      )}

      {graph ? (
        <GraphSection
          graph={graph}
          open={graphOpen}
          onToggle={() => setGraphOpen(!graphOpen)}
          nodeStatus={nodeStatus}
        />
      ) : (
        <div className="px-4 pb-3 text-xs text-[var(--on-surface-variant)] italic">
          (graph preview unavailable)
        </div>
      )}

      <div className="flex items-center justify-between px-4 py-2 border-t border-[var(--outline-variant)] bg-[var(--surface-lowest)]">
        <span className="font-mono text-[10px] text-[var(--on-surface-variant)]">
          run {runId.slice(0, 8)}
        </span>
        <span className="text-[10px] text-[var(--on-surface-variant)]">
          {isConnected ? '● live' : '○ connecting'}
        </span>
      </div>
    </div>
  );
}
